import React, { useMemo } from 'react';

// Helper to encode spaces in URLs for srcset (spaces break srcset parsing)
const encodeSrcsetUrl = (url) => url.replace(/ /g, '%20');

const ResponsiveImage = React.memo(({ 
    src, 
    alt, 
    style, 
    loading = 'lazy',
    priority = false,
    sizes = "(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33vw"
}) => {
    // Memoize WebP paths to avoid recalculating on every render
    const webpPaths = useMemo(() => {
        // Remove any existing extension
        const pathWithoutExt = src.replace(/\.(webp|jpg|jpeg|png)$/i, '');
        return {
            small: encodeSrcsetUrl(`${pathWithoutExt}-small.webp`),
            medium: encodeSrcsetUrl(`${pathWithoutExt}-medium.webp`),
            large: encodeSrcsetUrl(`${pathWithoutExt}-large.webp`),
            original: encodeSrcsetUrl(`${pathWithoutExt}.webp`)
        };
    }, [src]);

    // Memoize styles to prevent object recreation
    const imgStyle = useMemo(() => ({
        ...style,
        display: style?.display || 'block',
        height: style?.height || 'auto',
        width: style?.width || '100%',
    }), [style]);

    // Determine loading attributes based on priority
    const loadingAttr = priority ? 'eager' : loading;
    const fetchPriorityAttr = priority ? 'high' : 'auto';
    const decodingAttr = priority ? 'sync' : 'async';

    return (
        <picture style={{ display: 'block', width: '100%' }}>
            <source
                srcSet={`${webpPaths.small} 800w, ${webpPaths.medium} 1500w, ${webpPaths.large} 2400w`}
                sizes={sizes}
                type="image/webp"
            />
            <img
                src={webpPaths.original}
                alt={alt}
                style={imgStyle}
                loading={loadingAttr}
                fetchPriority={fetchPriorityAttr}
                decoding={decodingAttr}
            />
        </picture>
    );
}, (prevProps, nextProps) => {
    // Shallow comparison for primitive props
    if (prevProps.src !== nextProps.src) return false;
    if (prevProps.alt !== nextProps.alt) return false;
    if (prevProps.loading !== nextProps.loading) return false;
    if (prevProps.priority !== nextProps.priority) return false;
    if (prevProps.sizes !== nextProps.sizes) return false;
    
    // Deep comparison for style object
    const prevStyle = prevProps.style;
    const nextStyle = nextProps.style;
    if (prevStyle === nextStyle) return true;
    if (!prevStyle || !nextStyle) return prevStyle === nextStyle;
    
    const prevKeys = Object.keys(prevStyle);
    const nextKeys = Object.keys(nextStyle);
    if (prevKeys.length !== nextKeys.length) return false;
    
    return prevKeys.every(key => prevStyle[key] === nextStyle[key]);
});

ResponsiveImage.displayName = 'ResponsiveImage';

export default ResponsiveImage;